import { Dispatch, FC, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { Week52HighLowResponse } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getWeek52HighLowTopList as getWeek52HighLowTopListCall } from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import {
  isCallStatusError,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import Week52GainersAndLosers from '../../shared_components/Week52GainersAndLosers/Week52GainersAndLosers';
import { Week52Row } from '../../shared_components/Week52GainersAndLosers/Week52GainersAndLosers.types';
import { UI_ROUTES } from '../../util/routes';

const Week52GainersAndLosersWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getWeek52HighLowTopList,
    isWeek52HighLowTopListReady,
    isWeek52HighLowTopListError,
    week52HighLowTopList,
    companyLogoAndName,
  } = props;
  const { topNHigh, topNLow } = week52HighLowTopList;
  const isRequestCompleted = isWeek52HighLowTopListReady || isWeek52HighLowTopListError;

  const navigate = useNavigate();

  // Single market: the row's market fields are constants here, not resolved per symbol.
  const toRows = useMemo(() => (items: Week52HighLowResponse[] = []): Week52Row[] => (
    items.map((item) => {
      const entry = companyLogoAndName[item.symbol];

      return {
        ...item,
        lookupSymbol: item.symbol,
        currencySymbol: CurrencySymbolEnum.USD,
        isEuropeanSymbol: false,
        logo: entry?.logo ?? '',
        url: entry?.url ?? item.url ?? '',
        displayName: entry?.name ?? item.name ?? '',
      };
    })
  ), [companyLogoAndName]);

  const highRows = useMemo(() => toRows(topNHigh), [toRows, topNHigh]);
  const lowRows = useMemo(() => toRows(topNLow), [toRows, topNLow]);

  const handleViewAllClick = (isTopGainersSelected: boolean) => {
    navigate(UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow, { state: { isTopGainersSelected } });
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const symbolsToSubscribe = useMemo(() => {
    const allSymbols = [...(topNHigh || []), ...(topNLow || [])];

    return allSymbols.map((item) => ({
      symbol: item.symbol,
      stockExchangeCode: item.stockExchangeCode,
      countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
    }));
  }, [topNHigh, topNLow, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isWeek52HighLowTopListReady && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    getWeek52HighLowTopList();
  }, [getWeek52HighLowTopList]);

  return (
    <Week52GainersAndLosers
      topNHigh={highRows}
      topNLow={lowRows}
      isPending={!isRequestCompleted}
      isReady={isWeek52HighLowTopListReady}
      onViewAllClick={handleViewAllClick}
      onSymbolClick={handleSymbolClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isWeek52HighLowTopListReady: isCallStatusReady(
    FundamentalCall.getWeek52HighLowTopList,
    state.fundamental.statusByCall,
  ),
  isWeek52HighLowTopListError: isCallStatusError(
    FundamentalCall.getWeek52HighLowTopList,
    state.fundamental.statusByCall,
  ),
  week52HighLowTopList: state.fundamental.week52HighLowTopList,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getWeek52HighLowTopList: () => dispatch(getWeek52HighLowTopListCall()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Week52GainersAndLosersWrapper);
